import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TbArrowRight, TbCurrencyEuro, TbPlus } from 'react-icons/tb';
import Button from '../buttons/Button';
import * as bankAccountService from '../../../services/bankAccountService';
import {
  ContributionPlanWithMembersModel,
  ContributionPlanWithPayments,
} from '../../../types/bankAccount';
import { formatEuroCents } from '../../../utils/bankAccountUtil';

interface ContributionChartProps {
  onCreateContribution?: () => void;
}

function ContributionChart({ onCreateContribution }: ContributionChartProps): JSX.Element {
  const { t, i18n } = useTranslation('', {
    keyPrefix: 'page.financial.overview.contributionChart',
  });
  const navigate = useNavigate();
  const [contributionPlans, setContributionPlans] = useState<ContributionPlanWithMembersModel[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [plansWithPayments, setPlansWithPayments] = useState<ContributionPlanWithPayments[]>([]);

  // Fetch contribution plans
  useEffect(() => {
    const fetchContributionPlans = async () => {
      try {
        const plans = await bankAccountService.getAllContributionPlans();
        setContributionPlans(plans);
        // Fetch payments detail for each plan
        const detailed = await Promise.all(
          plans.map((p) =>
            bankAccountService.getContributionPlanWithPayments(p.contributionPlanId),
          ),
        );
        setPlansWithPayments(detailed);
      } catch (error) {
        console.error('Error fetching contribution plans:', error);
        setContributionPlans([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContributionPlans();
  }, []);

  const getPaidAmount = (plan: ContributionPlanWithPayments) => {
    return plan.contributionPayments.reduce((sum, payment) => sum + payment.transaction.amount, 0);
  };

  const topPlans = useMemo(() => {
    const sorted = [...plansWithPayments].sort((a, b) => getPaidAmount(b) - getPaidAmount(a));
    return sorted.slice(0, 5);
  }, [plansWithPayments]);

  const handleSeeMore = () => {
    navigate('/financial/contribution-plans');
  };

  const handleCreateContribution = () => {
    if (onCreateContribution) {
      onCreateContribution();
    } else {
      navigate('/financial/contribution-plans');
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t('title')}</h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('subtitle', { period: t('periodLabels.monthly') })}
          </p>
        </div>
        <button
          type="button"
          onClick={handleSeeMore}
          className="flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium text-primary transition-colors duration-200 hover:bg-primary/5">
          <span>{t('seeAllPlans')}</span>
          <TbArrowRight size={16} />
        </button>
      </div>

      {/* Body */}
      {!loading && contributionPlans.length === 0 ? (
        <div className="py-12 text-center">
          <TbCurrencyEuro className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">{t('empty.title')}</h3>
          <p className="mb-6 text-sm text-gray-500">{t('empty.subtitle')}</p>
          <Button variant="primary-dark" onClick={handleCreateContribution}>
            <TbPlus className="mr-2" />
            {t('empty.cta')}
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Paid</th>
              </tr>
            </thead>
            <tbody>
              {topPlans.map((plan) => (
                <tr
                  key={plan.contributionPlanId}
                  className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{plan.name}</td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                    {formatEuroCents(getPaidAmount(plan), i18n.language)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ContributionChart;
